// ── Plain fetch() API client — zero dependencies, zero CORS risk ─────
// No libraries, no default headers, no magic. Just fetch().

const API_BASE = (import.meta.env['VITE_API_BASE_URL'] as string) || 'http://localhost:3001';

async function get<T>(path: string, retries = 1): Promise<T> {
  const url = `${API_BASE}/${path}`;
  let lastError: Error | null = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url);
      if (res.ok) return res.json() as Promise<T>;
      // Retry on 500/502/503/504 (transient server errors, e.g. Lambda cold start)
      if (attempt < retries && res.status >= 500) {
        lastError = new Error(`GET /${path} failed (${res.status})`);
        await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
        continue;
      }
      const text = await res.text().catch(() => '');
      throw new Error(`GET /${path} failed (${res.status}): ${text}`);
    } catch (err) {
      if (attempt < retries && (err instanceof TypeError || (err as Error).message?.includes('fetch'))) {
        // Network error — retry
        lastError = err as Error;
        await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
        continue;
      }
      throw err;
    }
  }
  throw lastError ?? new Error(`GET /${path} failed after retries`);
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const url = `${API_BASE}/${path}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`POST /${path} failed (${res.status}): ${text}`);
  }
  return res.json() as Promise<T>;
}

// ── Types ──────────────────────────────────────────────────────────────

export interface AirportRef {
  iata: string;
  name: string;
  city: string;
}

export interface CountryResponse {
  iso: string;
  name: string;
  advisory_level: number;
  advisory_color: 'green' | 'yellow' | 'orange' | 'red';
  status: string;
  airports: string[];
  airport_details?: AirportRef[];
  airlines: string[];
  last_updated: string;
}

export interface AlertResponse {
  PK: string;
  SK: string;
  airline_iata: string;
  airline_name: string;
  airport_iata: string | null;
  country_iso: string | null;
  route: string | null;
  dates: string | null;
  status: 'suspended' | 'cancelled' | 'diverted' | 'resumed' | 'delayed' | 'rerouted';
  confidence: 'high' | 'medium' | 'low';
  summary: string | null;
  source_url: string | null;
  source_type?: string | null;
  advisory_level?: number | null;
  airport_name?: string | null;
  airport_city?: string | null;
  airport_airlines?: string[];
  airlines_at_airport?: string[] | null;
  last_updated: string;
}

export interface CrisisResponse {
  slug: string;
  name: string;
  description: string;
  crisis_type: string;
  core_countries: string[];
  regional_countries: string[];
  countries: string[];
  airports: string[];
  airlines: string[];
  cities: string[];
  severity: 1 | 2 | 3 | 4;
  last_updated: string;
}

export interface EscapeRoute {
  to_country: string;
  to_country_iso: string;
  drive_from: string;
  drive_hours: number;
  border_crossing: string;
  maps_link: string;
  airports: string[];
}

export interface CompensationPreview {
  alert_pk: string;
  airport: string;
  jurisdiction: string;
  short_haul: number;
  medium_haul: number;
  long_haul: number;
}

export interface CountryDetailResponse {
  country: {
    iso: string;
    name: string;
    advisory_level: number;
    advisory_color: 'green' | 'yellow' | 'orange' | 'red';
  };
  alerts: AlertResponse[];
  escape_routes?: EscapeRoute[];
  compensation_previews: CompensationPreview[];
}

export interface AirlineOperatingRoute {
  to: string;
  status: string;
  last_updated: string | null;
}

export interface AirlineOperating {
  code: string;
  name: string;
  status: 'operating' | 'disrupted';
  routes: AirlineOperatingRoute[];
  alerts: {
    status: string;
    route: string | null;
    summary: string | null;
    last_updated: string;
  }[];
}

export interface AirportDetailResponse {
  iata: string;
  name: string;
  city: string;
  country_iso: string;
  country_name?: string;
  advisory_level?: number;
  advisory_color?: string;
  operational_status: 'operating' | 'disrupted' | 'suspended' | 'closed' | 'unknown';
  airlines?: string[];
  airlines_operating?: AirlineOperating[];
  recent_alerts: AlertResponse[];
  lat?: number;
  lon?: number;
  icao?: string;
  type?: string;
  airlines_with_alerts?: string[];
}

export interface ChatResponse {
  reply: string;
  language: string;
  model: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

export interface PushSubscribeRequest {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  topics: {
    countries?: string[];
    airlines?: string[];
    airports?: string[];
    cities?: string[];
    crises?: string[];
  };
}

// ── API functions ──────────────────────────────────────────────────────

export async function fetchCountries(): Promise<CountryResponse[]> {
  const data = await get<{ countries: CountryResponse[] }>('countries');
  return data.countries;
}

export async function fetchCrises(): Promise<CrisisResponse[]> {
  const data = await get<{ crises: CrisisResponse[] }>('crises');
  return data.crises;
}

export async function fetchAlerts(): Promise<AlertResponse[]> {
  const data = await get<{ alerts: AlertResponse[] }>('alerts');
  return data.alerts;
}

export async function fetchCountryDetail(countryIso: string): Promise<CountryDetailResponse> {
  return get<CountryDetailResponse>(`alerts/${countryIso}`);
}

export async function sendChatMessage(
  message: string,
  language: string = 'en',
): Promise<ChatResponse> {
  return post<ChatResponse>('chat', { message, language });
}

export async function fetchAirportDetail(iata: string): Promise<AirportDetailResponse> {
  const data = await get<{ airport: AirportDetailResponse }>(`airport/${iata}`, 2);
  return data.airport;
}

export async function registerPushSubscription(req: PushSubscribeRequest): Promise<void> {
  await post<unknown>('subscribe', req);
}

// ── Search endpoint ────────────────────────────────────────────────

export interface SearchResult {
  kind: 'airport' | 'airline' | 'country';
  iata?: string;
  iso?: string;
  name: string;
  city?: string;
  country_iso?: string;
  country?: string;
  lat?: number;
  lon?: number;
  type?: string;
  advisory_level?: number;
  advisory_color?: string;
  operational_status?: string | null;
}

export async function searchAll(query: string): Promise<SearchResult[]> {
  const data = await get<{ results: SearchResult[] }>(`search?q=${encodeURIComponent(query)}`, 2);
  return data.results;
}

// ── Airlines endpoint ──────────────────────────────────────────────

export interface AirlineRef {
  iata: string;
  name: string;
  country: string | null;
}

export async function fetchAllAirlines(): Promise<AirlineRef[]> {
  const data = await get<{ airlines: AirlineRef[] }>('airlines');
  return data.airlines;
}
